import { FC, useRef } from 'react';
import { useFragmentContext } from '@client/app/context/FragmentContext';
import { isServer } from '@client/common/utils/env';

interface RemoteIslandProps {
  // Stable, page-unique: the DOM anchor the fragment bundle hydrates into, and the key its SSR
  // markup is stored under.
  id: string;
  className?: string;
}

// Embeds an isolated, server-rendered React fragment from another server as an island. SERVER
// seeds the inner html from what loadFragments fetched for this id; CLIENT seeds it from what the
// server already rendered into this node (no refetch). The fragment's own bundle then hydrates
// this container as a separate React root, so the host must not reconcile that subtree, hence
// suppressHydrationWarning + a ref-stable inner html.
export const RemoteIsland: FC<RemoteIslandProps> = ({ id, className }) => {
  const context = useFragmentContext();

  // Lazily initialized, not via the useRef initializer: reading innerHTML there would re-serialize
  // the whole subtree on every host re-render only to discard it.
  const ref = useRef<string | null>(null);
  if (ref.current === null) {
    ref.current = isServer
      ? (context?.islands[id]?.html ?? '')
      : (typeof document !== 'undefined' ? document.getElementById(id)?.innerHTML ?? '' : '');
  }
  const html = ref.current;

  return (
    <div
      id={id}
      className={className}
      suppressHydrationWarning
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};
